import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";

const COLUMNS = [
  { id: "submitted", title: "Submitted", color: "#0B67C2" },
  { id: "under_review", title: "Under Review", color: "#F59E0B" },
  { id: "proposal_sent", title: "Proposal Sent", color: "#1E40AF" },
  { id: "broadcasting", title: "Broadcasting", color: "#92400E" },
  { id: "partially_filled", title: "Partially Filled", color: "#9A3412" },
  { id: "fully_filled", title: "Filled", color: "#065F46" },
  { id: "completed", title: "Completed", color: "#166534" },
];

export default function DispatcherBoard() {
  const [requests, setRequests] = useState([]);
  const [facilities, setFacilities] = useState({});

  const load = () => {
    api.get("/requests").then((r) => setRequests(r.data));
    api.get("/facilities").then((r) => {
      const m = {}; r.data.forEach((f) => (m[f.id] = f)); setFacilities(m);
    });
  };
  useEffect(() => { load(); }, []);

  const byStatus = COLUMNS.reduce((acc, c) => {
    acc[c.id] = requests.filter((r) => r.status === c.id);
    return acc;
  }, {});

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, destination } = result;
    const newStatus = destination.droppableId;
    setRequests((rs) => rs.map((r) => (r.id === draggableId ? { ...r, status: newStatus } : r)));
    try {
      await api.patch(`/requests/${draggableId}`, { status: newStatus });
      toast.success(`Moved to ${newStatus.replace(/_/g, " ")}`);
    } catch { toast.error("Failed to update status"); load(); }
  };

  return (
    <div className="space-y-6" data-testid="dispatcher-board">
      <div>
        <h1 className="text-3xl font-bold text-brand-navy" style={{ fontFamily: "Outfit, sans-serif" }}>Dispatch Board</h1>
        <p className="text-slate-500 mt-1">Drag requests across stages. Status updates instantly.</p>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {COLUMNS.map((col) => (
            <div key={col.id} className="w-72 shrink-0">
              <div className="flex items-center justify-between mb-3 px-2">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: col.color }} />
                  <h3 className="font-semibold text-sm text-slate-700">{col.title}</h3>
                </div>
                <span className="text-xs text-slate-500 font-semibold">{byStatus[col.id]?.length || 0}</span>
              </div>
              <Droppable droppableId={col.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`min-h-[200px] p-2 rounded-xl ${snapshot.isDraggingOver ? "bg-teal-50" : "bg-slate-100"} transition-colors`}
                  >
                    {byStatus[col.id]?.map((r, i) => (
                      <Draggable key={r.id} draggableId={r.id} index={i}>
                        {(prov) => (
                          <div
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            {...prov.dragHandleProps}
                            className="kanban-card mb-2"
                          >
                            <Link to={`/dispatch/requests/${r.id}`}>
                              <Card className="p-3 bg-white border border-slate-200" data-testid={`kanban-card-${r.id}`}>
                                <div className="font-semibold text-sm text-slate-900">{r.position} × {r.count}</div>
                                <div className="text-xs text-slate-500 mt-1">{facilities[r.facility_id]?.name}</div>
                                <div className="text-xs text-slate-500 mt-1">{r.shift_date} · {r.start_time}</div>
                                {r.urgency !== "standard" && (
                                  <span className={`mt-2 inline-block text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${r.urgency === "emergency" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                                    {r.urgency}
                                  </span>
                                )}
                              </Card>
                            </Link>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          ))}
        </div>
      </DragDropContext>
    </div>
  );
}
